import React, { useRef } from 'react';
import { createPortal } from 'react-dom';
import { useWindowState, useWindowHandlers } from './WindowManagerContext';
import Window from './Window';

// A DEDICATED mount node for the window portal, owned exclusively by React.
//
// We must NOT portal straight into `document.body`: MUI (Popover/Menu/Tooltip),
// react-rnd's drag handling, and the dev-server error overlay all append to and
// reorder `document.body`'s direct children. When React later reconciles the
// portal it does `insertBefore(node, referenceSibling)` against body — and if
// that reference sibling was moved/removed by one of those other actors, the
// browser throws "Failed to execute 'insertBefore' on 'Node': The node before
// which the new node is to be inserted is not a child of this node."
//
// Giving the portal its own container that nothing else touches isolates
// React's sibling bookkeeping from the rest of body, so reconciliation can
// never reference a sibling that vanished out from under it.
function getPortalRoot() {
  if (typeof document === 'undefined') return null;
  let el = document.getElementById('wm-window-root');
  if (!el) {
    el = document.createElement('div');
    el.id = 'wm-window-root';
    // Zero-footprint host: it only parents fixed-position windows, so it must
    // not itself capture layout or pointer events.
    el.style.position = 'absolute';
    el.style.top = '0';
    el.style.left = '0';
    el.style.width = '0';
    el.style.height = '0';
    document.body.appendChild(el);
  }
  return el;
}

// =============================================================================
// WindowLayer — renders every open (non-minimized) window through a portal to
// document.body. Mount this ONCE, inside the WindowManagerProvider. Windows are
// position:fixed and self-stack via their z, so portal order doesn't matter; we
// still render in `order` (stable creation order) to keep React keys/
// reconciliation steady.
//
// The dock for MINIMIZED windows (Taskbar) is rendered SEPARATELY by the chat
// panel so it sits at the bottom of the messages area, not the viewport — see
// ChatPanel. It only needs the window-manager context, available app-wide.
//
// A window's CONTENT is produced by its stored `render(props)` factory — the
// layer is generic and never inspects what's inside.
// =============================================================================

// One window host: calls the per-id handlers hook (hooks can't run in a loop in
// the parent, so each window gets its own component instance).
const WindowHost = ({ win, focused }) => {
  const handlers = useWindowHandlers(win.id);
  // Produce the content node from the window's render factory + latest props.
  const content = win.render ? win.render(win.props || {}) : null;
  return (
    <Window win={win} focused={focused} handlers={handlers}>
      {content}
    </Window>
  );
};

const WindowLayer = () => {
  const state = useWindowState();
  // Resolve the dedicated portal host once and keep it stable for the lifetime
  // of this layer (the same node every render, never document.body).
  const rootRef = useRef(null);
  if (rootRef.current === null) rootRef.current = getPortalRoot();
  if (!rootRef.current) return null;

  const open = state.order
    .map((id) => state.windows[id])
    .filter((w) => w && !w.minimized);

  return createPortal(
    <>
      {open.map((win) => (
        <WindowHost key={win.id} win={win} focused={state.focusedId === win.id} />
      ))}
    </>,
    rootRef.current
  );
};

export default WindowLayer;
